import api from '@/utils/api'

/**
 * 营销活动V2 API服务
 */
class CampaignService {
  // V2 API 基础路径
  static BASE_URL = '/api/email-campaigns/v2'
  /**
   * 获取营销活动统计数据
   */
  async getStats() {
    try {
      const response = await api.get(`${CampaignService.BASE_URL}/campaigns/stats/`)
      return {
        success: true,
        data: response.data
      }
    } catch (error) {
      console.error('获取营销活动统计失败:', error)
      return {
        success: false,
        error: error.response?.data?.detail || '获取统计数据失败',
        status: error.response?.status,
        code: error.code
      }
    }
  }

  /**
   * 获取营销活动列表
   * @param {Object} params - 查询参数
   * @param {string} params.search - 搜索关键词
   * @param {string} params.status - 状态筛选
   * @param {number} params.page - 页码
   * @param {number} params.pageSize - 每页数量
   */
  async getCampaigns(params = {}) {
    try {
      const response = await api.get(`${CampaignService.BASE_URL}/campaigns/`, {
        params: {
          search: params.search || '',
          status: params.status || '',
          page: params.page || 1,
          page_size: params.pageSize || 20,
          ordering: params.ordering || '-created_at'
        }
      })
      return {
        success: true,
        data: response.data
      }
    } catch (error) {
      console.error('获取营销活动列表失败:', error)
      return {
        success: false,
        error: error.response?.data?.detail || '获取活动列表失败',
        status: error.response?.status,
        code: error.code
      }
    }
  }

  /**
   * 获取单个营销活动详情
   * @param {string|number} id - 活动ID
   */
  async getCampaign(id) {
    try {
      const response = await api.get(`${CampaignService.BASE_URL}/campaigns/${id}/`)
      return {
        success: true,
        data: response.data
      }
    } catch (error) {
      console.error('获取营销活动详情失败:', error)
      return {
        success: false,
        error: error.response?.data?.detail || '获取活动详情失败',
        status: error.response?.status,
        code: error.code
      }
    }
  }

  /**
   * 创建营销活动
   * @param {Object} data - 活动数据
   */
  async createCampaign(data) {
    try {
      const response = await api.post(`${CampaignService.BASE_URL}/campaigns/`, {
        name: data.name,
        description: data.description,
        template_id: data.template_id,
        subject: data.subject,
        from_account_ids: data.from_account_ids,
        use_smart_selection: data.use_smart_selection || false,  // 智能选择发件账户
        send_type: data.send_type,
        scheduled_time: data.scheduled_time || null,
        send_limit: data.send_limit || 0,  // 本次发送数量限制
        sort_method: data.sort_method || 'last_email_asc',  // 筛选方式
        target_all_customers: data.target_all_customers,
        include_tag_ids: data.include_tag_ids || [],
        exclude_tag_ids: data.exclude_tag_ids || [],
        add_to_existing_tags: data.add_to_existing_tags || [], // 添加到现有标签
        new_tags: data.new_tags || [], // 新建标签
        daily_limit: data.daily_limit || 500,
        send_interval_seconds: data.send_interval_seconds || 0.5,
        // 回复邮件模式字段
        reply_mode_enabled: data.reply_mode_enabled || false,
        reply_search_days: data.reply_search_days || 90,
        reply_prefer_received: data.reply_prefer_received !== undefined ? data.reply_prefer_received : true,
        // 自动化字段
        enable_automation: data.enable_automation || false,
        second_email_enabled: data.second_email_enabled || false,
        second_email_template: data.second_email_template || null,
        second_email_no_reply_hours: data.second_email_no_reply_hours || 24,
        second_email_read_no_reply_hours: data.second_email_read_no_reply_hours || 48,
        third_email_enabled: data.third_email_enabled || false,
        third_email_template: data.third_email_template || null,
        third_email_no_reply_hours: data.third_email_no_reply_hours || 72,
        third_email_read_no_reply_hours: data.third_email_read_no_reply_hours || 96
      })
      return {
        success: true,
        data: response.data
      }
    } catch (error) {
      console.error('创建营销活动失败:', error)
      return {
        success: false,
        error: error.response?.data?.detail || error.response?.data || '创建活动失败',
        status: error.response?.status,
        code: error.code,
        details: error.response?.data
      }
    }
  }

  /**
   * 更新营销活动
   * @param {string|number} id - 活动ID
   * @param {Object} data - 更新数据
   */
  async updateCampaign(id, data) {
    try {
      const response = await api.put(`${CampaignService.BASE_URL}/campaigns/${id}/`, {
        name: data.name,
        description: data.description,
        template_id: data.template_id,
        subject: data.subject,
        from_account_ids: data.from_account_ids,
        use_smart_selection: data.use_smart_selection || false,  // 智能选择发件账户
        send_type: data.send_type,
        scheduled_time: data.scheduled_time || null,
        send_limit: data.send_limit || 0,  // 本次发送数量限制
        sort_method: data.sort_method || 'last_email_asc',  // 筛选方式
        target_all_customers: data.target_all_customers,
        include_tag_ids: data.include_tag_ids || [],
        exclude_tag_ids: data.exclude_tag_ids || [],
        add_to_existing_tags: data.add_to_existing_tags || [], // 添加到现有标签
        new_tags: data.new_tags || [], // 新建标签
        daily_limit: data.daily_limit || 500,
        send_interval_seconds: data.send_interval_seconds || 0.5,
        // 回复邮件模式字段
        reply_mode_enabled: data.reply_mode_enabled || false,
        reply_search_days: data.reply_search_days || 90,
        reply_prefer_received: data.reply_prefer_received !== undefined ? data.reply_prefer_received : true,
        // 自动化字段
        enable_automation: data.enable_automation || false,
        second_email_enabled: data.second_email_enabled || false,
        second_email_template: data.second_email_template || null,
        second_email_no_reply_hours: data.second_email_no_reply_hours || 24,
        second_email_read_no_reply_hours: data.second_email_read_no_reply_hours || 48,
        third_email_enabled: data.third_email_enabled || false,
        third_email_template: data.third_email_template || null,
        third_email_no_reply_hours: data.third_email_no_reply_hours || 72,
        third_email_read_no_reply_hours: data.third_email_read_no_reply_hours || 96
      })
      return {
        success: true,
        data: response.data
      }
    } catch (error) {
      console.error('更新营销活动失败:', error)
      return {
        success: false,
        error: error.response?.data?.detail || error.response?.data || '更新活动失败',
        status: error.response?.status,
        code: error.code,
        details: error.response?.data
      }
    }
  }

  /**
   * 删除营销活动
   * @param {string|number} id - 活动ID
   */
  async deleteCampaign(id) {
    try {
      await api.delete(`${CampaignService.BASE_URL}/campaigns/${id}/`)
      return {
        success: true
      }
    } catch (error) {
      console.error('删除营销活动失败:', error)
      return {
        success: false,
        error: error.response?.data?.detail || '删除活动失败',
        status: error.response?.status,
        code: error.code
      }
    }
  }

  /**
   * 启动营销活动
   * @param {string|number} id - 活动ID
   */
  async startCampaign(id) {
    try {
      const response = await api.post(`${CampaignService.BASE_URL}/campaigns/${id}/start/`)
      return {
        success: true,
        data: response.data
      }
    } catch (error) {
      console.error('启动营销活动失败:', error)
      return {
        success: false,
        error: error.response?.data?.detail || error.response?.data?.error || '启动活动失败',
        status: error.response?.status,
        code: error.code
      }
    }
  }

  /**
   * 暂停营销活动
   * @param {string|number} id - 活动ID
   */
  async pauseCampaign(id) {
    try {
      const response = await api.post(`${CampaignService.BASE_URL}/campaigns/${id}/pause/`)
      return {
        success: true,
        data: response.data
      }
    } catch (error) {
      console.error('暂停营销活动失败:', error)
      return {
        success: false,
        error: error.response?.data?.detail || '暂停活动失败',
        status: error.response?.status,
        code: error.code
      }
    }
  }

  /**
   * 恢复营销活动（V2版本）
   * @param {string|number} id - 活动ID
   */
  async resumeCampaign(id) {
    try {
      const response = await api.post(`${CampaignService.BASE_URL}/campaigns/${id}/resume/`)
      return {
        success: true,
        data: response.data
      }
    } catch (error) {
      console.error('恢复营销活动失败:', error)
      return {
        success: false,
        error: error.response?.data?.detail || '恢复活动失败',
        status: error.response?.status,
        code: error.code
      }
    }
  }


  /**
   * 取消营销活动
   * @param {string|number} id - 活动ID
   */
  async cancelCampaign(id) {
    try {
      const response = await api.post(`/api/email_campaigns/campaigns/${id}/cancel/`)
      return {
        success: true,
        data: response.data
      }
    } catch (error) {
      console.error('取消营销活动失败:', error)
      return {
        success: false,
        error: error.response?.data?.detail || '取消活动失败',
        status: error.response?.status,
        code: error.code
      }
    }
  }

  /**
   * 复制营销活动
   * @param {string|number} id - 活动ID
   */
  async duplicateCampaign(id) {
    try {
      const response = await api.post(`${CampaignService.BASE_URL}/campaigns/${id}/duplicate/`)
      return {
        success: true,
        data: response.data
      }
    } catch (error) {
      console.error('复制营销活动失败:', error)
      return {
        success: false,
        error: error.response?.data?.detail || '复制活动失败',
        status: error.response?.status,
        code: error.code
      }
    }
  }

  /**
   * 预览目标客户
   * @param {Object} data - 预览参数
   */
  async previewCustomers(data) {
    try {
      const requestData = {
        target_all_customers: data.target_all_customers,
        include_tag_ids: data.include_tag_ids || [],
        exclude_tag_ids: data.exclude_tag_ids || []
      }
      
      const response = await api.post(`${CampaignService.BASE_URL}/campaigns/preview_customers/`, requestData)
      return response.data
    } catch (error) {
      console.error('预览目标客户失败:', error)
      throw error
    }
  }

  /**
   * 获取营销活动日志
   * @param {string|number} campaignId - 活动ID
   * @param {Object} params - 查询参数
   */
  async getCampaignLogs(campaignId, params = {}) {
    try {
      const response = await api.get(`${CampaignService.BASE_URL}/logs/`, {
        params: {
          campaign: campaignId,
          page: params.page || 1,
          page_size: params.per_page || params.pageSize || 20,
          status: params.status || '',
          email: params.email || '',  // 添加邮箱搜索参数
          ordering: params.ordering || '-created_at'
        }
      })
      return {
        success: true,
        data: response.data
      }
    } catch (error) {
      console.error('获取营销活动日志失败:', error)
      return {
        success: false,
        error: error.response?.data?.detail || '获取活动日志失败',
        status: error.response?.status,
        code: error.code
      }
    }
  }

  /**
   * 导出营销活动数据
   * @param {string|number} id - 活动ID
   * @param {string} format - 导出格式 (csv, excel)
   */
  async exportCampaignData(id, format = 'csv') {
    try {
      const response = await api.get(`/api/email_campaigns/campaigns/${id}/export/`, {
        params: { format },
        responseType: 'blob'
      })
      
      // 创建下载链接
      const blob = new Blob([response.data])
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      
      // 从响应头获取文件名
      const contentDisposition = response.headers['content-disposition']
      let filename = `campaign_${id}_data.${format}`
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/)
        if (filenameMatch && filenameMatch[1]) {
          filename = filenameMatch[1].replace(/['"]/g, '')
        }
      }
      
      link.setAttribute('download', filename)
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(url)
      
      return {
        success: true,
        message: '导出成功'
      }
    } catch (error) {
      console.error('导出营销活动数据失败:', error)
      return {
        success: false,
        error: error.response?.data?.detail || '导出数据失败',
        status: error.response?.status,
        code: error.code
      }
    }
  }

  /**
   * 获取邮件模板列表
   */
  async getTemplates() {
    try {
      const response = await api.get('/api/email_templates/templates/')
      return {
        success: true,
        data: response.data.results || response.data
      }
    } catch (error) {
      console.error('获取邮件模板失败:', error)
      return {
        success: false,
        error: error.response?.data?.detail || '获取模板失败',
        data: []
      }
    }
  }

  /**
   * 获取邮件账户列表
   */
  async getEmailAccounts() {
    try {
      const response = await api.get('/api/email_accounts/accounts/')
      return {
        success: true,
        data: response.data.results || response.data
      }
    } catch (error) {
      console.error('获取邮件账户失败:', error)
      return {
        success: false,
        error: error.response?.data?.detail || '获取邮件账户失败',
        data: []
      }
    }
  }

  /**
   * 获取客户标签列表
   */
  async getCustomerTags() {
    try {
      const response = await api.get(`${CampaignService.BASE_URL}/tags/`)
      return {
        success: true,
        data: response.data.results || response.data
      }
    } catch (error) {
      console.error('获取客户标签失败:', error)
      return {
        success: false,
        error: error.response?.data?.detail || '获取客户标签失败',
        data: []
      }
    }
  }

  /**
   * 获取邮件的自动化流程
   * @param {string|number} logId - 邮件日志ID
   */
  async getAutomationFlow(logId) {
    try {
      const response = await api.get(`${CampaignService.BASE_URL}/logs/${logId}/automation-flow/`)
      return {
        success: true,
        data: response.data
      }
    } catch (error) {
      console.error('获取自动化流程失败:', error)
      return {
        success: false,
        error: error.response?.data?.detail || '获取自动化流程失败',
        data: null
      }
    }
  }

}

// 创建单例实例
const campaignService = new CampaignService()

export default campaignService
